







































































import { Component, Prop, Vue } from 'vue-property-decorator';
import ImageTextVideoBlock from './ImageTextVideoBlock.vue';
import AdnuntiusPixels from '@/project/tracking/adnuntius/AdnuntiusPixels.vue';
import { PersonalizedContentBlockViewModel } from '@/types/contentServerContract';
import adnuntiusService from '@/project/personalization/adnuntius.service';
import { AdnuntiusTrackingData } from '@/project/tracking/adnuntius/types';
import themeService from '@/project/app/themes/theme.service';
import scrollService from '@/core/scroll/scroll.service';
import SmallImageTextBlock from '@/project/app/blocks/SmallImageTextBlock.vue';
import { AdnuntiusBannerObject, ExtendedAdContentBlockViewModel } from '@/project/app/blocks/AdnuntiusViewModel';
import breakpointState from '@/core/responsive/breakpoints/breakpointsState.observable';
import { MAX_SM } from '@/core/config/breakpointConfig';

@Component({
    components: { AdnuntiusPixels, ImageTextVideoBlock, SmallImageTextBlock }
})
export default class AdnuntiusHeroBannerBlock extends Vue {
    @Prop({
        required: true,
        type: Object
    }) content!: PersonalizedContentBlockViewModel;

    @Prop({
        required: true,
        type: Boolean
    }) isFirstBlock!: boolean;

    primaryBanner: AdnuntiusBannerObject = { adContent: null, loadError: false, trackingData: null, isFirst: this.isFirstBlock };
    small1Banner: AdnuntiusBannerObject = { adContent: null, loadError: false, trackingData: null, isFirst: false };
    small2Banner: AdnuntiusBannerObject = { adContent: null, loadError: false, trackingData: null, isFirst: false };

    get appendToPreviousBlock() {
        return this.content.appendToPreviousBlock && !this.isFirstBlock;
    }

    created() {
        this.loadAdContent();
    }

    imageLoaded() {
        // During take-over, a lot of re-rendering is going on with colors and element sizes, so we wait with the
        // big animation till the actual center is loaded
        setTimeout(() => {
            themeService.setThemeLoaded();
        }, 100);
    }

    async loadAdContent() {
        const requests: unknown[] = [];
        const callAdContent = async(adUnitId: string, feedback: { adContent: null | ExtendedAdContentBlockViewModel, trackingData: null | AdnuntiusTrackingData, loadError: boolean, isFirst?:boolean }): Promise<T> => {
            if (!adUnitId) return null;
            const request = this.getAdContent(adUnitId);
            request.then(result => {
                feedback.adContent = result;
                if (feedback.adContent && feedback.adContent.lineItemId && feedback.adContent.creativeId) {
                    feedback.trackingData = {
                        lineItemId: feedback.adContent.lineItemId,
                        creativeId: feedback.adContent.creativeId,
                        parentBlockAlias: 'personalizedContentHero'
                    };
                }
                if (feedback.isFirst) {
                    themeService.applyTheme({ ...feedback.adContent });
                }
            }).catch(() => {
                feedback.loadError = true;
            });
            return request;
        };

        requests.push(callAdContent(this.content.settings?.selectedAdUnitIdContent?.adUnitId, this.primaryBanner));
        requests.push(callAdContent(this.content.smallAdUnit1?.selectedAdUnitIdContent?.adUnitId, this.small1Banner));
        requests.push(callAdContent(this.content.smallAdUnit2?.selectedAdUnitIdContent?.adUnitId, this.small2Banner));
        await Promise.all(requests);

        if (this.isTakeOverBanner) {
            this.small1Banner.adContent = null;
            this.small2Banner.adContent = null;
        }
    }

    async getAdContent(adUnitId: string) {
        if (!adUnitId) return null;
        try {
            const adContentBlockViewModel: ExtendedAdContentBlockViewModel = await adnuntiusService.getBundledPersonalizedContent(
                adUnitId,
                this.$route.fullPath);
            adContentBlockViewModel.isBackgroundVideo = !!adContentBlockViewModel.vimeoVideoId; // To make ImageTextVideoBlock work, it must have video playing in background
            // hide callToAction if both overlayVimeoCta and overlayVimeoVideoId is set
            if (adContentBlockViewModel.overlayVimeoVideoId && adContentBlockViewModel.overlayVimeoCta && adContentBlockViewModel.callToAction) {
                adContentBlockViewModel.callToAction = undefined;
            }
            return adContentBlockViewModel;
        } catch (e) {
            return null;
        }
    }

    get isTakeOverBanner() {
        return themeService.isTakeOverEnabled;
    }

    get aspectRatio() {
        return this.isTakeOverBanner ? (1440 / 680) : (1440 / 480);
    }

    get marginStyle(): Partial<CSSStyleDeclaration> | undefined {
        const styles: Partial<CSSStyleDeclaration> = {};
        if (this.primaryBanner.adContent?.marginColor) {
            styles.backgroundColor = this.primaryBanner.adContent.marginColor;
        }
        if (this.isFirstBlock && !this.isMobile && this.isTakeOverBanner && this.isPrimaryBannerVideoBackground) {
            // Only add negative margin for take-over banners with video background, as image background is handled in ImageTextBlock.
            styles.marginTop = '-20rem';
        }

        return styles;
    }

    get isPrimaryBannerVideoBackground(): boolean {
        return !!(this.primaryBanner.adContent?.vimeoVideoId) || false;
    }

    get isMobile() {
        return breakpointState.isBreakpointActive(MAX_SM);
    }

    get leftImage() {
        return this.primaryBanner.adContent?.imageLeft?.url;
    }

    get scrollBarWidth() {
        return Math.floor(scrollService.getScrollbarWidth() / 2);
    }

    get leftImagePosition(): Partial<CSSStyleDeclaration> | undefined {
        if (!this.primaryBanner.adContent?.imageLeft) return undefined;

        return {
            transform: `translateX(calc((100vw - 1440px) / 2 - ${this.primaryBanner.adContent.imageLeft.width}px - ${this.scrollBarWidth}px))`
        };
    }

    get rightImage() {
        return this.primaryBanner.adContent?.imageRight?.url;
    }

    get rightImagePosition(): Partial<CSSStyleDeclaration> | undefined {
        if (!this.primaryBanner.adContent?.imageRight) return undefined;

        return {
            transform: `translateX(calc((1440px - 100vw) / 2 + ${this.primaryBanner.adContent.imageRight.width}px + ${this.scrollBarWidth}px))`
        };
    }
}
